import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { setChatText } from "app/pages/sales-intelligence/modules/ai-agent/store";
import type { SuperAgentThread } from "app/pages/sales-intelligence/modules/ai-agent/types/superAgent";
import useEnrichedRecents from "app/pages/sales-intelligence/modules/company-analysis/hooks/useEnrichedRecents";
import CompanyAutocompleteItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteItem/CompanyAutocompleteItem";
import CompanyAutocompleteUpgradeBlock from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteUpgradeBlock/CompanyAutocompleteUpgradeBlock";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { useIsCompanyLockedByQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useIsCompanyLockedByQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { AutocompleteAIItem } from "./AutocompleteAIItem";
import { AI_SUGGESTIONS } from "./constants";
import { StyledItemsContainer } from "./styles";

type Props = {
    onItemClick: (domain: string, isCompanyLevel: boolean) => void;
    recentAISearches: SuperAgentThread[];
    children: React.ReactNode;
};

export const EmptyState = ({ onItemClick, recentAISearches, children }: Props) => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();

    const { data: quota, isQuotaReached } = useCompanyQuota();
    const isCompanyLocked = useIsCompanyLockedByQuota();
    const { recentUniqueCompanies } = useEnrichedRecents(isQuotaReached ? 4 : undefined);

    const handleSuggestionItemClick = (suggestion: string) => {
        dispatch(setChatText(suggestion));
        AIAgentTrackingService.superAgent.trackSuggestionAISearchClick(suggestion);
        void siNavigator.goToSuperAgentPage();
    };

    const handleRecentAISearchClick = (id: string, text: string) => {
        AIAgentTrackingService.superAgent.trackRecentAISearchClick(text);
        void siNavigator.goToSuperAgentPage({ id });
    };

    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const renderRecentSites = () => {
        if (recentUniqueCompanies.length === 0) {
            return null;
        }
        return (
            <>
                <Text
                    fontVariant="captionTitle"
                    display="block"
                    margin="12px 12px 12px 20px"
                    color={colorsPalettes.carbon[250]}
                >
                    {translate("common.recent.searches")}
                </Text>

                {recentUniqueCompanies.map((recent) =>
                    recent.companyInfo ? (
                        <CompanyAutocompleteItem
                            key={recent.companyInfo?.companyName}
                            company={recent}
                            hideLabel
                            onCompanyClick={({ key }) => onItemClick(key, true)}
                            onWebsiteClick={(domain) => onItemClick(domain, false)}
                            isLocked={isCompanyLocked(recent.isPurchased)}
                        />
                    ) : null,
                )}
                {!isDataCreditsEnabled && isQuotaReached ? (
                    <CompanyAutocompleteUpgradeBlock interval={quota?.interval} />
                ) : null}
            </>
        );
    };

    const renderAIItems = () => {
        if (recentAISearches?.length) {
            return recentAISearches.map((recent) => (
                <AutocompleteAIItem
                    key={recent.threadId}
                    text={recent.threadSubject}
                    onClick={() => handleRecentAISearchClick(recent.threadId, recent.threadSubject)}
                />
            ));
        }
        return AI_SUGGESTIONS.map((suggestion) => (
            <AutocompleteAIItem
                key={suggestion.label}
                text={translate(suggestion.label)}
                onClick={() => handleSuggestionItemClick(translate(suggestion.value))}
            />
        ));
    };

    const isSuperAgentEnabled = salesSettingsHelper.isSuperAgentEnabled;

    const shouldRender = isSuperAgentEnabled || recentUniqueCompanies.length > 0 || children !== null;

    if (!shouldRender) {
        return null;
    }

    return (
        <div className="ListItemsContainer">
            <StyledItemsContainer>
                {isSuperAgentEnabled ? (
                    <Text
                        fontVariant="captionTitle"
                        display="block"
                        margin="12px 12px 12px 20px"
                        color={colorsPalettes.carbon[250]}
                    >
                        {translate(
                            recentAISearches?.length
                                ? "si.super-agent.ai-suggestions.recents.title"
                                : "si.super-agent.ai-suggestions.title",
                        )}
                    </Text>
                ) : null}
                {isSuperAgentEnabled ? renderAIItems() : null}
                {renderRecentSites()}
                {children}
            </StyledItemsContainer>
        </div>
    );
};
